<template>
    <div class="flex-1 flex flex-col bg-gray-50">
        <!-- 头部 -->
        <div class="bg-white border-b border-gray-200 p-6">
            <div class="flex items-center justify-between">
                <div>
                    <h1 class="text-2xl font-bold text-gray-900">我的收藏</h1>
                    <p class="text-gray-600 mt-1">管理您收藏的对话内容</p>
                </div>
                <div class="flex items-center space-x-4">
                    <span class="text-sm text-gray-500">共 {{ totalCount }} 条收藏</span>
                    <button 
                        @click="goBackToChat"
                        class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center space-x-2"
                    >
                        <i class="fas fa-arrow-left"></i>
                        <span>返回对话</span>
                    </button>
                </div>
            </div>
        </div>

        <!-- 收藏列表 -->
        <div class="flex-1 overflow-y-auto p-6">
            <div v-if="isLoading" class="flex justify-center items-center h-64">
                <div class="text-center">
                    <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p class="text-gray-600">正在加载收藏列表...</p>
                </div>
            </div>

            <div v-else-if="favorites.length === 0" class="text-center py-16">
                <div class="text-gray-400 mb-4">
                    <i class="fas fa-star text-6xl"></i>
                </div>
                <h3 class="text-xl font-semibold text-gray-900 mb-2">暂无收藏</h3>
                <p class="text-gray-600 mb-6">您还没有收藏任何对话内容</p>
                <button 
                    @click="goBackToChat"
                    class="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
                >
                    开始对话
                </button>
            </div>

            <div v-else class="space-y-4">
                <!-- 收藏项 -->
                <div 
                    v-for="favorite in favorites" 
                    :key="favorite.id"
                    class="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200 cursor-pointer"
                    @click="showFavoriteDetail(favorite)"
                >
                    <div class="p-6">
                        <div class="flex items-start justify-between">
                            <div class="flex-1 min-w-0">
                                <!-- 问题预览 -->
                                <h3 class="text-lg font-medium text-gray-900 mb-2 line-clamp-2">
                                    {{ decodeUnicode(favorite.questionPreview) }}
                                </h3>
                                
                                <!-- 收藏时间 -->
                                <div class="flex items-center text-sm text-gray-500">
                                    <i class="fas fa-clock mr-2"></i>
                                    <span>{{ formatDateTime(favorite.createdTime) }}</span>
                                </div>
                            </div>
                            
                            <!-- 操作按钮 -->
                            <div class="flex items-center space-x-2 ml-4">
                                <button 
                                    @click.stop="removeFavorite(favorite.id)"
                                    class="p-2 text-gray-400 hover:text-red-500 transition-colors duration-200"
                                    title="删除收藏"
                                >
                                    <i class="fas fa-trash"></i>
                                </button>
                                <button 
                                    @click.stop="showFavoriteDetail(favorite)"
                                    class="p-2 text-gray-400 hover:text-blue-500 transition-colors duration-200"
                                    title="查看详情"
                                >
                                    <i class="fas fa-eye"></i>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- 加载更多 -->
                <div v-if="hasMore" class="text-center py-6">
                    <button 
                        @click="loadMore"
                        :disabled="isLoadingMore"
                        class="px-6 py-3 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <span v-if="isLoadingMore">加载中...</span>
                        <span v-else>加载更多</span>
                    </button>
                </div>
            </div>
        </div>

        <!-- 收藏详情弹窗 -->
        <div v-if="showDetailModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div class="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[80vh] flex flex-col">
                <!-- 弹窗头部 -->
                <div class="flex items-center justify-between p-6 border-b border-gray-200">
                    <h2 class="text-xl font-semibold text-gray-900">收藏详情</h2>
                    <button 
                        @click="closeDetailModal"
                        class="p-2 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                    >
                        <i class="fas fa-times text-xl"></i>
                    </button>
                </div>

                <!-- 弹窗内容 -->
                <div class="flex-1 overflow-y-auto p-6">
                    <div v-if="selectedFavorite" class="space-y-6">
                        <!-- 问题 -->
                        <div>
                            <h3 class="text-lg font-medium text-gray-900 mb-3 flex items-center">
                                <i class="fas fa-question-circle text-blue-600 mr-2"></i>
                                问题
                            </h3>
                            <div class="bg-blue-50 rounded-lg p-4">
                                <p class="text-gray-800 whitespace-pre-wrap">{{ decodeUnicode(selectedFavorite.question) }}</p>
                            </div>
                        </div>

                        <!-- 回答 -->
                        <div>
                            <h3 class="text-lg font-medium text-gray-900 mb-3 flex items-center">
                                <i class="fas fa-robot text-green-600 mr-2"></i>
                                回答
                            </h3>
                            <div class="bg-green-50 rounded-lg p-4">
                                <div class="text-gray-800" v-html="renderMarkdown(selectedFavorite.answer)"></div>
                            </div>
                        </div>

                        <!-- 收藏信息 -->
                        <div class="bg-gray-50 rounded-lg p-4">
                            <div class="flex items-center justify-between text-sm text-gray-600">
                                <div class="flex items-center">
                                    <i class="fas fa-star text-yellow-500 mr-2"></i>
                                    <span>收藏时间：{{ formatDateTime(selectedFavorite.createdTime) }}</span>
                                </div>
                                <div class="flex items-center space-x-4">
                                    <button 
                                        @click="copyToClipboard(decodeUnicode(selectedFavorite.question) + '\n\n' + decodeUnicode(selectedFavorite.answer))"
                                        class="text-blue-600 hover:text-blue-800 transition-colors duration-200"
                                    >
                                        <i class="fas fa-copy mr-1"></i>
                                        复制内容
                                    </button>
                                    <button 
                                        @click="removeFavorite(selectedFavorite.id)"
                                        class="text-red-600 hover:text-red-800 transition-colors duration-200"
                                    >
                                        <i class="fas fa-trash mr-1"></i>
                                        删除收藏
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { marked } from 'marked';
import DOMPurify from 'dompurify';

export default {
    name: 'Favorites',
    data() {
        return {
            favorites: [],
            isLoading: false,
            isLoadingMore: false,
            currentPage: 0,
            pageSize: 10,
            hasMore: true,
            totalCount: 0,
            showDetailModal: false,
            selectedFavorite: null,
            md: null
        }
    },
    async mounted() {
        // 初始化markdown渲染器
        this.md = marked;
        this.md.setOptions({
            gfm: true,
            breaks: true,
            highlight: function (code, lang) {
                return code;
            }
        });
        
        await this.loadFavorites();
    },
    methods: {
        // 加载收藏列表
        async loadFavorites() {
            this.isLoading = true;
            try {
                const token = localStorage.getItem('token');
                if (!token) {
                    alert('请先登录');
                    this.$router.push('/login');
                    return;
                }

                const response = await fetch(`${process.env.VUE_APP_API_BASE_URL}/api/favorites/list?page=0&size=${this.pageSize}`, {
                    headers: {
                        'Authorization': token
                    }
                });

                const result = await response.json();
                if (result.code === 200) {
                    this.favorites = result.data.data || [];
                    this.totalCount = result.data.totalCount || 0;
                    this.hasMore = result.data.totalPages > 1;
                    this.currentPage = 0;
                } else {
                    alert(result.message || '加载收藏列表失败');
                }
            } catch (error) {
                console.error('加载收藏列表失败:', error);
                alert('加载收藏列表失败，请重试');
            } finally {
                this.isLoading = false;
            }
        },

        // 加载更多收藏
        async loadMore() {
            if (this.isLoadingMore || !this.hasMore) {
                return;
            }

            this.isLoadingMore = true;
            try {
                const token = localStorage.getItem('token');
                const nextPage = this.currentPage + 1;

                const response = await fetch(`${process.env.VUE_APP_API_BASE_URL}/api/favorites/list?page=${nextPage}&size=${this.pageSize}`, {
                    headers: {
                        'Authorization': token
                    }
                });

                const result = await response.json();
                if (result.code === 200) {
                    this.favorites = [...this.favorites, ...result.data];
                    this.hasMore = nextPage < result.totalPages - 1;
                    this.currentPage = nextPage;
                } else {
                    alert(result.message || '加载更多失败');
                }
            } catch (error) {
                console.error('加载更多失败:', error);
                alert('加载更多失败，请重试');
            } finally {
                this.isLoadingMore = false;
            }
        },

        // 显示收藏详情
        showFavoriteDetail(favorite) {
            this.selectedFavorite = favorite;
            this.showDetailModal = true;
        },

        // 关闭详情弹窗
        closeDetailModal() {
            this.showDetailModal = false;
            this.selectedFavorite = null;
        },

        // 删除收藏
        async removeFavorite(favoriteId) {
            try {
                const token = localStorage.getItem('token');
                const response = await fetch(`${process.env.VUE_APP_API_BASE_URL}/api/favorites/${favoriteId}`, {
                    method: 'DELETE',
                    headers: {
                        'Authorization': token
                    }
                });

                const result = await response.json();
                if (result.code === 200) {
                    alert('删除收藏成功');
                    // 从列表中移除
                    this.favorites = this.favorites.filter(f => f.id !== favoriteId);
                    this.totalCount--;
                    
                    // 如果当前显示的是被删除的收藏，关闭详情弹窗
                    if (this.selectedFavorite && this.selectedFavorite.id === favoriteId) {
                        this.closeDetailModal();
                    }
                    
                    // 通知左侧导航更新收藏数量
                    this.$emit('favorite-count-changed');
                    // 同时使用自定义事件通知
                    window.dispatchEvent(new CustomEvent('favoriteCountUpdated'));
                } else {
                    alert(result.message || '删除收藏失败');
                }
            } catch (error) {
                console.error('删除收藏失败:', error);
                alert('删除收藏失败，请重试');
            }
        },

        // 复制到剪贴板
        async copyToClipboard(text) {
            try {
                await navigator.clipboard.writeText(text);
                alert('内容已复制到剪贴板');
            } catch (error) {
                console.error('复制失败:', error);
                alert('复制失败，请手动复制');
            }
        },

        // 返回对话界面
        goBackToChat() {
            this.$router.push('/chat');
        },

        // 格式化日期时间
        formatDateTime(dateTime) {
            if (!dateTime) return '';
            const date = new Date(dateTime);
            return date.toLocaleString('zh-CN', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
            });
        },

        // Unicode解码方法
        decodeUnicode(content) {
            if (typeof content !== 'string') return content;
            
            // 解码Unicode格式的字符串
            // 优先处理 \uXXXX 格式，如果不存在则处理 uXXXX 格式
            if (content.includes('\\u')) {
                // 如果包含 \uXXXX 格式，使用这种格式解码
                return content.replace(/\\u([0-9a-fA-F]{4})/g, (match, hex) => {
                    return String.fromCharCode(parseInt(hex, 16));
                });
            } else if (content.includes('u') && /u[0-9a-fA-F]{4}/.test(content)) {
                // 如果包含 uXXXX 格式，使用这种格式解码
                return content.replace(/u([0-9a-fA-F]{4})/g, (match, hex) => {
                    return String.fromCharCode(parseInt(hex, 16));
                });
            }
            
            // 如果都不包含，返回原内容
            return content;
        },

        // 渲染Markdown
        renderMarkdown(content) {
            if (!content) return '';
            // 先解码Unicode，再渲染Markdown
            const decodedContent = this.decodeUnicode(content);
            const html = marked(decodedContent);
            return DOMPurify.sanitize(html);
        }
    }
}
</script>

<style scoped>
.line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}
</style>
